import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid } from '@mui/material';
import CardContentEditable from 'components/StudiesDashboard/CardContentEditable';
import { PipsExpectedResults } from 'interfaces/calculateFeatures';
import { cloneDeep, findIndex, map, pullAt, set } from 'lodash';
import { default as React } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { humanize, uuidv4 } from 'utils/helpers';
import { Plot } from './Plot';
import { ConfigurablePlotParams, GenerateStudyReportFormValues } from './types';

export interface PlotsStepProps {
  studyId: string;
  pipsExpectedResults: PipsExpectedResults;
  selectedPipsOrchestrationId?: string;
  isPipsExpectedResultsLoading: boolean;
  methods: UseFormReturn<GenerateStudyReportFormValues, any>;
  checkValidationAndSetIsStepFailed: () => void;
}

export const PlotsStep: React.FC<PlotsStepProps> = ({
  studyId,
  pipsExpectedResults,
  selectedPipsOrchestrationId,
  isPipsExpectedResultsLoading,
  methods,
  checkValidationAndSetIsStepFailed,
}) => {
  const { watch, setValue } = methods;

  const addPlot = (newPlot: ConfigurablePlotParams, featuresPlotIndex: number) => {
    const currentPlots = watch('configParams.configurablePlots') || [];
    const newPlots = cloneDeep(currentPlots);
    set(newPlots, [featuresPlotIndex, 'plotList'], [...(newPlots[featuresPlotIndex].plotList || []), newPlot]);
    setValue('configParams.configurablePlots', newPlots);
    checkValidationAndSetIsStepFailed();
  };

  const defaultNewPlots: ConfigurablePlotParams[] = [
    {
      id: uuidv4(),
      plotType: 'scatter',
      xAxis: '',
      yAxis: '',
      includeTrendline: false,
    },
    {
      id: uuidv4(),
      plotType: 'histogram',
      column: '',
      bins: null,
    },
    {
      id: uuidv4(),
      plotType: 'violin',
      columns: [],
    },
    {
      id: uuidv4(),
      plotType: 'box',
      columns: [],
    },
    {
      id: uuidv4(),
      plotType: 'pie',
      column: '',
    },
    {
      id: uuidv4(),
      plotType: 'quantification_analysis',
    },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent, featuresPlotIndex: number) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const currentPlots = watch('configParams.configurablePlots') || [];
      const newPlots = cloneDeep(currentPlots);
      const oldIndex = findIndex(newPlots[featuresPlotIndex].plotList, { id: String(active.id) });
      const newIndex = findIndex(newPlots[featuresPlotIndex].plotList, { id: String(over.id) });
      set(
        newPlots,
        [featuresPlotIndex, 'plotList'],
        arrayMove(newPlots[featuresPlotIndex].plotList, oldIndex, newIndex)
      );
      setValue('configParams.configurablePlots', newPlots);
      checkValidationAndSetIsStepFailed();
    }
  };

  return (
    <Grid container direction="column" spacing={2}>
      {map(watch('configParams.configurablePlots') || [], (featuresPlot, featuresPlotIndex) => (
        <Grid item key={featuresPlotIndex}>
          <Accordion disableGutters key={featuresPlotIndex} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid container alignItems="center" alignContent={'center'} justifyContent={'space-between'} spacing={2}>
                <Grid item>
                  <CardContentEditable
                    disabled={false}
                    savedValue={featuresPlot.plotName}
                    onSave={(newValue: string) => {
                      const currentPlots = watch('configParams.configurablePlots') || [];
                      const newPlots = cloneDeep(currentPlots);
                      newPlots[featuresPlotIndex].plotName = newValue;
                      setValue('configParams.configurablePlots', newPlots);
                      checkValidationAndSetIsStepFailed();
                    }}
                    onEditModeChange={() => {}}
                  />
                </Grid>
                <Grid item>
                  <DeleteOutlineIcon
                    sx={{
                      cursor: 'pointer',
                      mr: 2,
                    }}
                    onClick={() => {
                      const currentPlots = cloneDeep(watch('configParams.configurablePlots')) || [];
                      pullAt(currentPlots, featuresPlotIndex);
                      setValue('configParams.configurablePlots', currentPlots);
                      checkValidationAndSetIsStepFailed();
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => handleDragEnd(event, featuresPlotIndex)}
              >
                <SortableContext items={featuresPlot.plotList} strategy={verticalListSortingStrategy}>
                  <Grid container direction="column" spacing={2}>
                    {map(featuresPlot.plotList, (plot, plotIndex) => (
                      <Grid item key={plotIndex}>
                        <Plot
                          studyId={studyId}
                          orchestrationId={selectedPipsOrchestrationId}
                          methods={methods}
                          plot={plot}
                          plotIndex={plotIndex}
                          featuresPlotIndex={featuresPlotIndex}
                          featuresKeys={pipsExpectedResults?.expectedFeatures || []}
                          isLoadingFeaturesData={isPipsExpectedResultsLoading}
                          checkValidationAndSetIsStepFailed={checkValidationAndSetIsStepFailed}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </SortableContext>
              </DndContext>
              <Grid container spacing={2} mt={1}>
                {map(defaultNewPlots, (defaultNewPlot) => {
                  return (
                    <Grid item key={defaultNewPlot.plotType}>
                      <Button
                        onClick={() => {
                          addPlot(defaultNewPlot, featuresPlotIndex);
                        }}
                      >
                        {`Add ${humanize(defaultNewPlot.plotType)} Chart`}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}

      <Grid item>
        <Button
          aria-describedby="simple-popover"
          onClick={() => {
            const currentPlots = watch('configParams.configurablePlots') || [];
            setValue(
              'configParams.configurablePlots',
              currentPlots.concat([
                {
                  plotName: `Analysis Plot ${currentPlots.length + 1}`,
                  plotList: [],
                },
              ])
            );
            checkValidationAndSetIsStepFailed();
          }}
        >
          Add New Analysis plots
        </Button>
      </Grid>
    </Grid>
  );
};
